import {
	asyncWithLDProvider,
	useFlags,
	useLDClient,
} from 'launchdarkly-react-client-sdk';

import {
	USER_CREATED_AT,
	UTM_CONTENT,
	UTM_CAMPAIGN,
	UUID_COOKIE_NAME,
	STASH_PROMO_CODE,
	STASH_PROMO_CODE_V2,
} from 'stash/constants/session';
import { LAUNCH_DARKLY_CLIENT_IDS } from 'stash/constants';
import { getCookie } from 'stash/utils/cookies';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { userSelector } from 'stash/selectors/user';
import { useEffect, useRef } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { isMobileWeb } from 'stash/utils';
import { getLocalStorage } from '../../utils/storage.js';

/**
 * Experiment keys of current Launch Darkly ab tests.
 */
export const EXPERIMENTS = {
	REPLACE_CHAT_BANNER_WITH_ICON: 'web-2023-02-27-ai-chat-icon-on-every-screen',
	DEFAULT_MONTHLY_BILLING_FREQUENCY:
		'web-2025-03-18-default-to-monthly-billing-frequency',
	HIDE_HEADER_ON_MOBILE: 'web-2025-03-18-hide-header-mobile-web',
	USER_SIGN_UP_MULTI_VARIANT: 'web-2025-03-31-user-sign-up-multi-variant-test',
	SMART_PORTFOLIO_REDESIGN: 'web-2025-05-02-redesign-smart-portfolio-in-onboarding',
	AUTO_STASH_REDESIGN_IN_OB: 'web-2025-06-23-auto-stash-redesign-in-ob',
	ONBOARDING_BANK_LINK_ITERATIONS_V2:
		'web-2025-12-05-bank-link-iterations-v-2-onboarding',
	IS_BUSTING_WEBVIEWS_ENABLED: 'web-reg-busting-webviews-enabled',
	STATE_ZERO_REDESIGN: 'web-2025-06-25-state-zero-redesign',
	PERSONAL_PORTFOLIO_REDESIGN: 'web-2025-06-12-personal-portfolio-redesign',
};

export const USER_SIGN_UP_VARIATIONS = {
	SHOW_WELCOME_CAROUSEL_SCREEN: 'welcome-carousel',
	SHOW_SIGNUP_SCREEN: 'direct-to-signup',
	SHOW_INTENT_QUESTION_SCREEN: 'intent-question',
};

export const TIER_SELECTION_VARIATIONS = {
	CONTROL: 'control-$3-option-first-no-peek',
	VARIANT_A: 'variant-a-$9-option-first-no-peek',
	VARIANT_B: 'variant-b-$3-option-first-with-peek',
	VARIANT_C: 'variant-c-$9-option-first-with-peek',
};

function createMultiContext(uuid, createDate, utmContent, utmCampaign, stashPromoCode) {
	const userContext = createUserContext(uuid, createDate);
	const deviceContext = createDeviceContext(uuid);
	const referralContext = createReferralContext(
		uuid,
		utmContent,
		utmCampaign,
		stashPromoCode
	);
	return {
		kind: 'multi',
		user: userContext,
		device: deviceContext,
		referral: referralContext,
	};
}

function createUserContext(uuid, createDate) {
	const baseContext = createBaseContext(uuid, 'user');
	return {
		...baseContext,
		createDate,
	};
}

/**
 * Device context is needed for targeting mobile web.
 * We cannot use the mobile SDK for mobile web.
 */
function createDeviceContext(uuid) {
	const baseContext = createBaseContext(uuid, 'device');
	return {
		...baseContext,
		isMobile: isMobileWeb(),
	};
}

function createReferralContext(uuid, utmContent, utmCampaign, stashPromoCode) {
	const baseContext = createBaseContext(uuid, 'referral');
	return {
		...baseContext,
		// Launch Darkly requires a default value for the custom rules to function properly.
		utmContent: utmContent || 'no-utm-content', //example: dot-com_investments_NVDA
		utmCampaign: utmCampaign || 'no-utm-campaign', //example: getfive
		stashPromoCode: stashPromoCode || 'no-stash-promo-code', //example: stash promo referral code
	};
}

/**
 * Every LD context must have a consistent, unique key tied to the person.
 * If the user context is anonymous, LD will generate a key.
 * @see https://docs.launchdarkly.com/sdk/features/context-config
 */
function createBaseContext(uuid, kind) {
	const context = {
		kind,
	};

	if (uuid) {
		context.key = kind === 'user' ? uuid : `${kind}-key-${uuid}`;
	} else {
		context.anonymous = true;
	}
	return context;
}

export const getLaunchDarklyProvider = () => {
	const clientSideID = LAUNCH_DARKLY_CLIENT_IDS[window.Stash.env];

	return asyncWithLDProvider({
		context: createMultiContext(
			getCookie(UUID_COOKIE_NAME),
			getCookie(USER_CREATED_AT),
			getCookie(UTM_CONTENT),
			getCookie(UTM_CAMPAIGN),
			getCookie(STASH_PROMO_CODE_V2) || getCookie(STASH_PROMO_CODE)
		),
		clientSideID,
		options: {
			streaming: false,
			disableSyncEventPost: true,
			inspectors: [
				{
					type: 'flag-used',
					name: 'dd-inspector',
					method: (key, detail) => {
						datadogRum.addFeatureFlagEvaluation(key, detail.value);
					},
				},
			],
		},
		reactOptions: {
			useCamelCaseFlagKeys: false,
		},
	});
};

export function ExperimentsContextUpdater() {
	const mountedRef = useRef(false);
	const client = useLDClient();
	const user = useSelector(userSelector);

	useEffect(() => {
		if (mountedRef.current) {
			client.identify(
				createMultiContext(
					user?.uuid,
					user?.created_at,
					getCookie(UTM_CONTENT),
					getCookie(UTM_CAMPAIGN),
					getCookie(STASH_PROMO_CODE_V2) || getCookie(STASH_PROMO_CODE)
				)
			);
		}

		mountedRef.current = true;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.uuid, user?.created_at]);
	return null;
}

function useCurrentVariant(experiment) {
	const flags = useFlags();
	const localTestVariant = getLocalStorage(experiment);
	const hasLocalTestVariant = localTestVariant !== null && localTestVariant !== undefined;

	if (hasLocalTestVariant) {
		switch (localTestVariant) {
			case 'default':
				return undefined;
			case 'false':
				return false;
			case 'true':
				return true;
			default:
				return localTestVariant;
		}
	}

	return flags[experiment];
}

/**
 * Renders DOM elements when in the provided experiment variant.
 */
export function ExperimentVariant({ experiment, variant, defaultVariant, children }) {
	const currentVariant = useCurrentVariant(experiment);

	if (defaultVariant && currentVariant === undefined) {
		return children;
	}

	return variant === currentVariant ? children : null;
}

/**
 * Returns true if the user is in the provided experiment variant.
 * For pure business logic; useful when not rendering DOM elements.
 */
export function useIsInExperimentVariation(experiment, variant) {
	const currentVariant = useCurrentVariant(experiment);
	return variant === currentVariant;
}

/**
 * Return the variant string the user is in.
 * Useful for a/b/c tests when you just want the variant string the user has been assigned,
 * */
export function useCurrentExperimentVariant(experiment) {
	return useCurrentVariant(experiment);
}

const VariantPropType = PropTypes.oneOfType([PropTypes.bool, PropTypes.string]);

ExperimentVariant.propTypes = {
	experiment: PropTypes.string.isRequired,
	variant: VariantPropType.isRequired,
	defaultVariant: PropTypes.bool,
	children: PropTypes.node.isRequired,
};
